import { useEffect, useState, type ChangeEvent } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import jazzcashLogo from "../Assets/Images/jcLogo.webp";

interface UnsubscribePopupProps {
  show: boolean;
  value: string;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => void;
  onConfirmText: string;
  onCancelText?: string;
  onConfirm: (flag: boolean, recaptchaTokenParam?: string) => void;
  onCancel?: () => void;
}

const PHONE_REGEX = /^3\d{2}[-\s]?\d{7}$/;
const ACCENT = "#e74c3c"; // matches FailurePopUp's button color for this project

const UnsubscribePopup = (props: UnsubscribePopupProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTouched(false);
  }, [props.show]);

  if (!props.show) return null;

  const error = !PHONE_REGEX.test(props.value)
    ? "Enter a valid JazzCash number"
    : undefined;
  const showError = touched && error;

  const handleBack = () => {
    props.onCancel ? props.onCancel() : props.onConfirm(false);
  };

  const handleSubmit = async () => {
    if (!executeRecaptcha) {
      return;
    }
    setTouched(true);
    if (error) {
      return;
    }
    try {
      setIsLoading(true);
      const recaptchaToken = await executeRecaptcha("guest_unsubscribe");
      props.onConfirm(true, recaptchaToken);
    } catch (err) {
      console.log("recaptcha error::", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={handleBack}>
      <style>{spinnerKeyframes}</style>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            style={styles.backBtn}
          >
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#111"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 style={styles.title}>Unsubscribe</h2>
        </div>

        <p style={styles.subtitle}>Enter your Jazzcash number</p>

        <div style={styles.inputWrap}>
          <div
            style={{
              ...styles.inputBox,
              ...(showError
                ? styles.inputBoxError
                : focused
                  ? styles.inputBoxFocused
                  : {}),
            }}
          >
            <span style={styles.logoWrap}>
              <img src={jazzcashLogo} alt="JazzCash" style={styles.logoImg} />
            </span>
            <input
              id="jazzcashNumber"
              name="jazzcashNumber"
              type="tel"
              inputMode="numeric"
              placeholder="3XX - XXXXXXX"
              value={props.value}
              onChange={props.onChange}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                setFocused(false);
                setTouched(true);
              }}
              maxLength={12}
              style={styles.input}
            />
          </div>

          {showError && <p style={styles.errorText}>{error}</p>}

          <div style={styles.recaptchaBadge}>Protected by reCAPTCHA</div>
        </div>

        <div style={styles.actions}>
          {props.onCancelText && (
            <button
              style={styles.outlineButton}
              onClick={handleBack}
              disabled={isLoading}
            >
              {props.onCancelText}
            </button>
          )}
          <button
            style={{
              ...styles.button,
              flex: props.onCancelText ? 1 : undefined,
            }}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? <span style={styles.spinner} /> : props.onConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const spinnerKeyframes = `
@keyframes unsub-modal-spin {
  to { transform: rotate(360deg); }
}
`;

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "28px 28px 32px",
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 24,
  },
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111",
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: "#555",
    margin: "0 0 16px",
    lineHeight: 1.6,
  },
  inputWrap: {
    marginBottom: 24,
  },
  inputBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderWidth: "1.5px",
    borderStyle: "solid",
    borderColor: "#e2e2e2",
    borderRadius: 12,
    padding: "12px 16px",
    transition: "border-color 0.15s ease",
  },
  inputBoxFocused: {
    borderColor: ACCENT,
  },
  inputBoxError: {
    borderColor: ACCENT,
  },
  logoWrap: {
    display: "flex",
    alignItems: "center",
    paddingRight: 10,
    borderRight: "1.5px solid #e2e2e2",
  },
  logoImg: {
    height: 20,
    objectFit: "contain",
  },
  input: {
    border: "none",
    outline: "none",
    fontSize: 16,
    color: "#111",
    flex: 1,
    minWidth: 0,
  },
  errorText: {
    color: ACCENT,
    fontSize: 13,
    margin: "8px 0 0",
  },
  recaptchaBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 10,
    padding: "10px 14px",
    background: "#f7f7f7",
    borderRadius: 10,
    fontSize: 13,
    textAlign: "center",
    color: "#555",
  },
  actions: {
    display: "flex",
    gap: 12,
  },
  button: {
    flex: 1,
    padding: "14px",
    backgroundColor: ACCENT,
    color: "#fff",
    fontSize: 16,
    fontWeight: 600,
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
  },
  outlineButton: {
    flex: 1,
    padding: "14px",
    backgroundColor: "#fff",
    color: "#111",
    fontSize: 16,
    fontWeight: 600,
    border: "1.5px solid #e2e2e2",
    borderRadius: 12,
    cursor: "pointer",
  },
  spinner: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2.5px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    animation: "unsub-modal-spin 0.7s linear infinite",
    display: "inline-block",
  },
};

export default UnsubscribePopup;
